import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  apiListIntakes, apiCreateIntake, apiUpdateIntake, apiDeleteIntake,
  apiListPublicIntakes,
  apiListDistributions, apiCreateDistribution, apiUpdateDistribution, apiDeleteDistribution,
  apiListNeeds, apiCreateNeed, apiUpdateNeed, apiDeleteNeed,
  CATEGORIES, CATEGORY_COLOR, PUBLIC_STATUS_STYLE,
  publicStatusForIntake,
} from "./api";
import { apiListDepartments, apiUpdateDepartment } from "../departments/api";
import DonationFormModal from "./DonationFormModal";
import IntakeTab from "./IntakeTab";
import DistributionTab from "./DistributionTab";
import CommunityNeedsTab from "./CommunityNeedsTab";
import PublicListings from "./PublicListings";
import ReportsTab from "./ReportsTab";
import FoodFormBuilderTab from "./FoodFormBuilderTab";
import FoodEntryDetailModal from "./FoodEntryDetailModal";
import { CustomIcon } from "../shared/CustomIcon";

// ============================================================
// DonationsPage — accessed via the Departments grid (any dept with
// `isDonations: true`). Two sides:
//   • Public  — card listing, any logged-in user.
//   • Private — Intake / Distribution / Needs tabs, dept members + leader
//               + Limited Admins + Super Admins.
// ============================================================
export default function DonationsPage({ currentUser, isSuperAdmin, isLimitedAdmin, members, users, addNotification, navigate }) {
  const { id: deptIdFromUrl } = useParams();
  const [dept, setDept] = useState(null);
  const [loading, setLoading] = useState(true);
  const [side, setSide] = useState("public");      // 'public' | 'private'
  const [privateTab, setPrivateTab] = useState("intake"); // 'intake' | 'distribution' | 'needs' | 'reports'

  const [intakes, setIntakes] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [publicIntakes, setPublicIntakes] = useState([]);

  const [modal, setModal] = useState(null); // { kind, existing }
  const [foodEntry, setFoodEntry] = useState(null); // Food Contribution Registration detail modal
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [renameError, setRenameError] = useState("");

  // --- Load dept + storage data ---
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const all = await apiListDepartments();
        let found = (all || []).find(d => d.id === deptIdFromUrl) || null;
        if (!found) found = (all || []).find(d => d.isDonations) || null;
        if (alive) {
          setDept(found);
          setDraftName(found ? found.name : "");
        }
        // Compute whether to bother fetching private endpoints. This mirrors
        // the canSeePrivate check below and prevents 403 console noise for
        // non-dept members (the backend correctly rejects with 403; we just
        // don't ask for those records in the first place).
        const isMem = !!(found && Array.isArray(found.users) && found.users.includes(currentUser.id));
        const isLeader = !!(found && found.leaderUserId === currentUser.id);
        const wantPrivate = isSuperAdmin || isLimitedAdmin || isMem || isLeader;

        const [ints, dists, nds, pubInts] = await Promise.all([
          wantPrivate ? apiListIntakes()        : Promise.resolve([]),
          wantPrivate ? apiListDistributions()  : Promise.resolve([]),
          wantPrivate ? apiListNeeds()          : Promise.resolve([]),
          apiListPublicIntakes(),
        ]);
        if (alive) {
          setIntakes(ints === null ? [] : (ints || []));
          setDistributions(dists === null ? [] : (dists || []));
          setNeeds(nds === null ? [] : (nds || []));
          setPublicIntakes(pubInts || []);
        }
      } catch (_) { /* ignore — empty arrays are fine */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [deptIdFromUrl]);

  // --- Permissions ---
  // Department membership is now based on userId (not member). Leader is also a userId.
  const isDeptMember = useMemo(() => {
    if (!dept) return false;
    return Array.isArray(dept.users) && dept.users.includes(currentUser.id);
  }, [dept, currentUser.id]);

  const isDeptLeader = useMemo(() => {
    if (!dept) return false;
    return dept.leaderUserId && dept.leaderUserId === currentUser.id;
  }, [dept, currentUser.id]);

  const canSeePrivate = !!(isSuperAdmin || isLimitedAdmin || isDeptLeader || isDeptMember);
  const canRenameDept = !!(isSuperAdmin || isDeptLeader);

  // --- Save helpers ---
  // Each helper hits the dedicated endpoint (so notifications + access
  // control fire server-side) and then refreshes the in-memory list.
  const refreshIntakes = async () => {
    const [priv, pub] = await Promise.all([apiListIntakes(), apiListPublicIntakes()]);
    setIntakes(priv === null ? [] : (priv || []));
    setPublicIntakes(pub || []);
  };
  const refreshDistributions = async () => {
    const d = await apiListDistributions();
    setDistributions(d === null ? [] : (d || []));
    // A distribution change may flip an intake's derived public status.
    setPublicIntakes(await apiListPublicIntakes() || []);
  };
  const refreshNeeds = async () => {
    const n = await apiListNeeds();
    setNeeds(n === null ? [] : (n || []));
  };

  // --- Rename dept ---
  const submitRename = async () => {
    setRenameError("");
    const trimmed = draftName.trim();
    if (!trimmed) { setRenameError("Name cannot be empty."); return; }
    try {
      const updated = await apiUpdateDepartment(dept.id, { name: trimmed });
      setDept(updated);
      setRenaming(false);
    } catch (e) {
      setRenameError(e.message || "Could not save");
    }
  };

  // --- Public listings derived data (uses server-sanitized public endpoint) ---
  const publicListings = useMemo(() => (
    (publicIntakes || []).map(i => ({ ...i, _publicStatus: publicStatusForIntake(i, distributions) }))
  ), [publicIntakes, distributions]);

  // --- Render ---
  if (loading) {
    return (
      <div data-testid="donations-page-loading" style={{ padding: 40, textAlign: "center", color: "var(--kc-meta)" }}>
        Loading donations…
      </div>
    );
  }

  if (!dept) {
    return (
      <div data-testid="donations-page-missing" style={{ padding: 40 }}>
        <div className="widget-empty-add">
          <div className="wea-icon"><CustomIcon name="tools" size={40} /></div>
          <div className="wea-title">No Donations department found</div>
          <div className="wea-sub">A Super Admin can enable any department as the Donations module via the Department form.</div>
          <button className="btn btn-outline" onClick={() => navigate("/departments")}>← Back to Departments</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="donations-page">
      <style>{DONATIONS_CSS}</style>

      {/* Header */}
      <header className="donations-header">
        <div className="donations-back">
          <button type="button" className="donations-back-link" data-testid="donations-back" onClick={() => navigate("/departments")}>← Departments</button>
        </div>
        <div className="donations-title-row">
          {!renaming ? (
            <>
              <h1 className="donations-title" data-testid="donations-title">{dept.name}</h1>
              {canRenameDept && (
                <button type="button" className="donations-rename-btn" data-testid="donations-rename-btn" onClick={() => { setDraftName(dept.name); setRenaming(true); setRenameError(""); }} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <CustomIcon name="edit" size={14} /> Rename
                </button>
              )}
            </>
          ) : (
            <div className="donations-rename-row">
              <input
                data-testid="donations-rename-input"
                className="donations-rename-input"
                value={draftName}
                onChange={e => setDraftName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && submitRename()}
                autoFocus
              />
              <button className="btn btn-gold btn-sm" data-testid="donations-rename-save" onClick={submitRename}>Save</button>
              <button className="btn btn-outline btn-sm" data-testid="donations-rename-cancel" onClick={() => setRenaming(false)}>Cancel</button>
              {renameError && <span style={{ color: "#ff9b9b", fontSize: 11 }}>{renameError}</span>}
            </div>
          )}
        </div>
        <p className="donations-subtitle">Specialised donations workflow — public listing + private intake / distribution / community-needs registry.</p>
      </header>

      {/* Side switcher */}
      <div className="donations-side-switcher" role="tablist" aria-label="Donations side">
        <button
          type="button"
          data-testid="donations-side-public"
          role="tab"
          aria-selected={side === "public"}
          className={`dss-btn ${side === "public" ? "active" : ""}`}
          onClick={() => setSide("public")}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <CustomIcon name="globe" size={14} /> Public Listings <span className="dss-badge">{publicListings.length}</span>
        </button>
        {canSeePrivate && (
          <button
            type="button"
            data-testid="donations-side-private"
            role="tab"
            aria-selected={side === "private"}
            className={`dss-btn ${side === "private" ? "active" : ""}`}
            onClick={() => setSide("private")}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <CustomIcon name="lock" size={14} /> Private Workspace
          </button>
        )}
      </div>

      {/* Public side */}
      {side === "public" && (
        <PublicListings
          listings={publicListings}
          dept={dept}
          currentUser={currentUser}
          users={users}
          addNotification={addNotification}
        />
      )}

      {/* Private side */}
      {side === "private" && canSeePrivate && (
        <div data-testid="donations-private">
          <div className="donations-private-tabs" role="tablist">
            {[
              { id: "intake",       label: "Intake Log",       count: intakes.length },
              { id: "distribution", label: "Distribution Log", count: distributions.length },
              { id: "needs",        label: "Community Needs",  count: needs.length },
              { id: "reports",      label: "Reports",          count: null },
              { id: "food_form",    label: "Food Form Builder", count: null },
            ].map(t => (
              <button
                key={t.id}
                type="button"
                role="tab"
                data-testid={`donations-tab-${t.id}`}
                aria-selected={privateTab === t.id}
                className={`donations-private-tab ${privateTab === t.id ? "active" : ""}`}
                onClick={() => setPrivateTab(t.id)}
              >
                {t.label}{t.count !== null && <span className="dpt-count">{t.count}</span>}
              </button>
            ))}
          </div>

          {privateTab === "intake" && (
            <IntakeTab
              intakes={intakes}
              currentUser={currentUser}
              users={users}
              onDelete={async (it) => { await apiDeleteIntake(it.id); await refreshIntakes(); }}
              onNew={() => setModal({ kind: "intake", existing: null })}
              onEdit={(it) => setModal({ kind: "intake", existing: it })}
            />
          )}
          {privateTab === "distribution" && (
            <DistributionTab
              distributions={distributions}
              intakes={intakes}
              currentUser={currentUser}
              users={users}
              onDelete={async (d) => { await apiDeleteDistribution(d.id); await refreshDistributions(); }}
              onNew={() => setModal({ kind: "distribution", existing: null })}
              onEdit={(it) => setModal({ kind: "distribution", existing: it })}
            />
          )}
          {privateTab === "needs" && (
            <CommunityNeedsTab
              needs={needs}
              currentUser={currentUser}
              onDelete={async (n) => { await apiDeleteNeed(n.id); await refreshNeeds(); }}
              onNew={() => setModal({ kind: "need", existing: null })}
              onEdit={(it) => {
                if (it.source === "food_form") setFoodEntry(it);
                else setModal({ kind: "need", existing: it });
              }}
            />
          )}
          {privateTab === "reports" && (
            <ReportsTab
              currentUser={currentUser}
              isSuperAdmin={isSuperAdmin}
              isLimitedAdmin={isLimitedAdmin}
              isDeptLeader={isDeptLeader}
            />
          )}
          {privateTab === "food_form" && (
            <FoodFormBuilderTab
              isSuperAdmin={isSuperAdmin}
              canManage={isSuperAdmin || isDeptLeader}
            />
          )}
        </div>
      )}

      {side === "private" && !canSeePrivate && (
        <div className="donations-locked" data-testid="donations-locked" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CustomIcon name="lock" size={16} /> Only department members, the department leader, Limited Admins, and Super Admins can access the private workspace.
        </div>
      )}

      {/* Form modal */}
      {modal && (
        <DonationFormModal
          kind={modal.kind}
          existing={modal.existing}
          currentUser={currentUser}
          users={users}
          intakes={intakes}
          onClose={() => setModal(null)}
          onSave={async (data) => {
            try {
              if (modal.kind === "intake") {
                if (modal.existing) await apiUpdateIntake(modal.existing.id, data);
                else                await apiCreateIntake(data);
                await refreshIntakes();
              } else if (modal.kind === "distribution") {
                if (modal.existing) await apiUpdateDistribution(modal.existing.id, data);
                else                await apiCreateDistribution(data);
                await refreshDistributions();
              } else if (modal.kind === "need") {
                if (modal.existing) await apiUpdateNeed(modal.existing.id, data);
                else                await apiCreateNeed(data);
                await refreshNeeds();
              }
              return true;
            } catch (e) {
              throw e;
            }
          }}
        />
      )}
      {foodEntry && (
        <FoodEntryDetailModal
          entry={foodEntry}
          canEdit={canSeePrivate}
          onClose={() => setFoodEntry(null)}
          onSaved={async (updated) => {
            await refreshNeeds();
            setFoodEntry(updated);
          }}
        />
      )}
    </div>
  );
}

// --- CSS scoped to the Donations module ---
const DONATIONS_CSS = `
  .donations-header { margin-bottom: 18px; }
  .donations-back { margin-bottom: 8px; }
  .donations-back-link { background: transparent; border: none; color: var(--kc-sub); font-family: 'Montserrat',sans-serif; font-size: 12px; cursor: pointer; padding: 0; }
  .donations-back-link:hover { color: var(--kc-accent); }
  .donations-title-row { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
  .donations-title { font-family: 'Cinzel', serif; font-size: 28px; font-weight: 700; color: var(--kc-text); letter-spacing: 0.08em; text-transform: uppercase; margin: 0; }
  @media (min-width: 720px) { .donations-title { font-size: 34px; } }
  .donations-rename-btn { background: transparent; border: 1px solid color-mix(in srgb, var(--kc-accent) 45%, transparent); color: var(--kc-accent); border-radius: 8px; padding: 6px 10px; cursor: pointer; font-size: 11px; font-family: 'Montserrat',sans-serif; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
  .donations-rename-btn:hover { background: color-mix(in srgb, var(--kc-accent) 10%, transparent); }
  .donations-rename-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .donations-rename-input { background: var(--kc-surface); border: 1px solid color-mix(in srgb, var(--kc-accent) 45%, transparent); color: var(--kc-text); border-radius: 8px; padding: 8px 12px; font-family: 'Cinzel', serif; font-size: 22px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; min-width: 240px; }
  .donations-subtitle { font-family: 'Montserrat',sans-serif; font-size: 13px; color: var(--kc-sub); margin: 8px 0 0; max-width: 720px; }

  .donations-side-switcher { display: flex; gap: 8px; margin-bottom: 22px; flex-wrap: wrap; }
  .dss-btn { background: var(--kc-elevated); border: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent); color: var(--kc-text); border-radius: 12px; padding: 11px 18px; font-family: 'Montserrat',sans-serif; font-weight: 700; font-size: 12px; letter-spacing: 0.06em; text-transform: uppercase; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: border-color 0.15s, background 0.15s; }
  .dss-btn:hover { border-color: color-mix(in srgb, var(--kc-accent) 55%, transparent); background: color-mix(in srgb, var(--kc-accent) 8%, transparent); }
  .dss-btn.active { background: var(--kc-accent); color: #1A1A1A; border-color: var(--kc-accent); }
  .dss-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 22px; height: 18px; padding: 0 6px; border-radius: 999px; background: rgba(0,0,0,0.20); color: inherit; font-family: 'Cinzel', serif; font-size: 11px; font-weight: 700; }
  .dss-btn:not(.active) .dss-badge { background: color-mix(in srgb, var(--kc-accent) 18%, transparent); color: var(--kc-accent); }

  .donations-private-tabs { display: flex; gap: 4px; flex-wrap: wrap; border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent); margin-bottom: 18px; }
  .donations-private-tab { background: transparent; border: none; padding: 11px 14px; color: var(--kc-sub); font-family: 'Montserrat',sans-serif; font-size: 12px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; transition: color 0.15s, border-color 0.15s; }
  .donations-private-tab:hover { color: var(--kc-accent); }
  .donations-private-tab.active { color: var(--kc-accent); border-bottom-color: var(--kc-accent); }
  .donations-private-tab .dpt-count { display: inline-flex; align-items: center; justify-content: center; margin-left: 6px; min-width: 20px; padding: 0 6px; height: 16px; border-radius: 999px; background: color-mix(in srgb, var(--kc-accent) 18%, transparent); color: var(--kc-accent); font-family: 'Cinzel',serif; font-size: 10px; font-weight: 700; }
  .donations-private-tab.active .dpt-count { background: var(--kc-accent); color: #1A1A1A; }

  .donations-locked { background: rgba(255,255,255,0.04); border: 1px dashed rgba(255,255,255,0.15); border-radius: 12px; padding: 28px; text-align: center; color: var(--kc-sub); font-family: 'Montserrat',sans-serif; font-size: 13px; }

  /* Donations toolbar (add button + filters) */
  .don-toolbar { display: flex; gap: 10px; justify-content: space-between; align-items: center; flex-wrap: wrap; margin-bottom: 14px; }
  .don-toolbar-filters { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
  .don-filter-select { background: var(--kc-surface); border: 1px solid rgba(255,255,255,0.10); color: var(--kc-text); border-radius: 8px; padding: 7px 10px; font-family: 'Montserrat',sans-serif; font-size: 12px; }
  .don-filter-label { font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--kc-sub); margin-right: 4px; }

  /* List rows */
  .don-list { display: flex; flex-direction: column; gap: 12px; }
  /* Card layout uses vertical sections so long names/donor strings never
     overlap with the badges or buttons below. Top row: icon + title.
     Below: meta, badges, actions, visibility — each on its own line. */
  .don-row { background: var(--kc-elevated); border: 1px solid color-mix(in srgb, var(--kc-accent) 16%, transparent); border-radius: 12px; padding: 14px; display: flex; flex-direction: column; gap: 10px; cursor: pointer; transition: border-color 0.12s, transform 0.12s; }
  .don-row:hover { border-color: color-mix(in srgb, var(--kc-accent) 45%, transparent); transform: translateY(-1px); }
  .don-row-top { display: flex; align-items: center; gap: 12px; }
  .don-row-icon { width: 44px; height: 44px; border-radius: 10px; background: color-mix(in srgb, var(--kc-accent) 12%, transparent); border: 1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent); display: inline-flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
  .don-row-main { min-width: 0; flex: 1; }
  .don-row-title { font-family: 'Cinzel',serif; font-size: 14px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--kc-text); word-wrap: break-word; overflow-wrap: anywhere; }
  .don-row-meta { font-family: 'Montserrat',sans-serif; font-size: 11px; color: var(--kc-sub); margin-top: 4px; display: flex; gap: 10px; flex-wrap: wrap; }
  .don-row-chips { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
  .don-row-actions { display: flex; gap: 6px; flex-wrap: wrap; padding-top: 4px; border-top: 1px solid rgba(255,255,255,0.04); }
  .don-row-visibility { font-family: 'Montserrat',sans-serif; font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; font-weight: 700; color: var(--kc-meta); }
  @media (max-width: 480px) {
    .don-row-meta { font-size: 10px; gap: 6px; }
    .don-row-actions .btn { flex: 1; }
  }
  .don-chip { padding: 3px 9px; border-radius: 999px; font-family: 'Montserrat',sans-serif; font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; border: 1px solid currentColor; }

  .don-empty { background: rgba(255,255,255,0.03); border: 1px dashed rgba(255,255,255,0.10); border-radius: 12px; padding: 28px; text-align: center; color: var(--kc-sub); font-family: 'Montserrat',sans-serif; font-size: 13px; }

  /* Donation form modal */
  .donation-modal { width: min(560px, 100%); }
  .donation-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 12px; }
  .donation-field .df-label { font-size: 10px; font-weight: 700; letter-spacing: 0.10em; text-transform: uppercase; color: var(--kc-sub); }
  .donation-field input, .donation-field select, .donation-field textarea {
    background: var(--kc-surface); border: 1px solid rgba(255,255,255,0.10); color: var(--kc-text);
    border-radius: 9px; padding: 10px 12px; font-family: 'Montserrat',sans-serif; font-size: 13px;
    transition: border-color 0.15s;
  }
  .donation-field input:focus, .donation-field select:focus, .donation-field textarea:focus { border-color: var(--kc-accent); outline: none; }
  .donation-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  @media (max-width: 480px) { .donation-field-row { grid-template-columns: 1fr; } }
  .donation-toggle { display: inline-flex; align-items: center; gap: 8px; padding: 8px 0; cursor: pointer; font-size: 13px; color: var(--kc-text); font-family: 'Montserrat',sans-serif; }
  .donation-section-label { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 700; color: var(--kc-accent); letter-spacing: 0.10em; text-transform: uppercase; margin-bottom: 8px; }

  /* Public listings */
  .pl-grid { display: grid; gap: 14px; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); }
  .pl-card { background: var(--kc-elevated); border: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent); border-radius: 14px; overflow: hidden; display: flex; flex-direction: column; transition: border-color 0.15s, transform 0.15s; }
  .pl-card:hover { border-color: color-mix(in srgb, var(--kc-accent) 55%, transparent); transform: translateY(-2px); }
  .pl-card-photo { aspect-ratio: 16/10; background: var(--kc-surface); display: flex; align-items: center; justify-content: center; color: var(--kc-meta); font-size: 38px; }
  .pl-card-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .pl-card-body { padding: 14px; display: flex; flex-direction: column; gap: 8px; flex: 1; }
  .pl-card-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
  .pl-cat-badge { padding: 2px 8px; border-radius: 999px; font-size: 9px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; font-family: 'Montserrat',sans-serif; }
  .pl-status-badge { padding: 2px 8px; border-radius: 999px; font-size: 9px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; font-family: 'Montserrat',sans-serif; border: 1px solid; }
  .pl-card-title { font-family: 'Cinzel',serif; font-size: 14px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--kc-text); }
  .pl-card-desc { font-family: 'Montserrat',sans-serif; font-size: 12px; color: var(--kc-sub); line-height: 1.45; flex: 1; }
  .pl-card-btn { margin-top: 4px; }
  .pl-empty { grid-column: 1 / -1; background: rgba(255,255,255,0.03); border: 1px dashed rgba(255,255,255,0.10); border-radius: 12px; padding: 36px; text-align: center; color: var(--kc-sub); font-family: 'Montserrat',sans-serif; font-size: 13px; }
`;
